import React from 'react';
import { IntlProvider } from 'react-intl';
import { GetStaticProps, NextPage } from 'next';
import ErrorPage from 'next/error';
import { useRouter } from 'next/router';
import { Layout } from '@raisin/cp-layout';
import { getLocale, Locale } from '@raisin/cp-utils';
import { getServerSideTranslations, setClientSideTranslations } from '../../i18n';
import { BankPageContent } from '../components/BankPageContent';
import { PageContent } from '../components/PageContent';
import PressPageContent from '../components/PressPage';
import { PressReleasePageContent } from '../components/PressReleaseContent';
import { PageContentTypes, PageTemplateProps, PageType } from '../types';
import { getContentStackPageProps } from '../utils/contentStackPageUtils/getContentStackPageProps';
import { getPageStaticPaths } from '../utils/pageStaticPaths';
import { getBankPageProps, getPageType, NotFoundError } from '../utils/pagesUtils';
import { useLivePreview } from '../utils/useLivePreview';

const PageTemplate: NextPage<PageTemplateProps> = ({
  bankPage,
  banks,
  contentData,
  contentEntries,
  headingData,
  isCorporate,
  isLoginRequired,
  layout,
  mediaArticles,
  modelPortfolios,
  page,
  pressReleases,
  pressPageData,
  productInformation,
  sidebarData,
  locale,
  messages,
  usProductOffers,
}) => {
  const { asPath } = useRouter();
  setClientSideTranslations(messages);
  const pageContent = useLivePreview({ contentData, locale, pageId: page?.uid });

  if (!layout) {
    return null;
  }

  const footerGap = true;

  if (bankPage) {
    return (
      <BankPageContent
        bankPage={bankPage}
        banks={banks}
        asPath={asPath}
        locale={locale}
        layout={layout}
        messages={messages.translations}
      />
    );
  }

  if (!page) {
    return <ErrorPage statusCode={404} />;
  }

  const {
    title: pageTitle,
    navigation_settings: navigationSettings,
    seo_settings: seoSettings,
  } = page;
  layout.headerData.hideMainNavigation = navigationSettings?.hide_main_navigation;
  layout.footerData.hideFooterLinks = navigationSettings?.hide_footer_links ?? false;
  layout.footerData.hideFooter = navigationSettings?.hide_footer;

  if (seoSettings?.opengraph?.image) {
    seoSettings.opengraph.image.url = new URL(seoSettings.opengraph.image.url);
  }

  const Pages = {
    [PageContentTypes.PAGES]: PageContent,
    [PageContentTypes.PRESS_RELEASE_PAGE]: PressReleasePageContent,
    [PageContentTypes.DEPOSITS_OFFERS_TABLE]: PageContent,
    [PageContentTypes.PRESS_PAGE]: PressPageContent,
  };

  const CurrentPage = Pages[page?._content_type_uid];

  return (
    <IntlProvider messages={messages.translations} locale={locale}>
      <Layout
        {...{ layout, pageTitle, seoSettings, footerGap }}
        isPortal={false}
        isAuthRequired={isLoginRequired}
        isBypassAuthContext={false}
        isCorporate={isCorporate}
        isSmartBannerPage
        themeVersion={page.theme_version === 'v2' ? 'v2' : undefined}
        pageMessages={messages.translations}
      >
        <CurrentPage
          headingData={headingData}
          contentData={pageContent}
          sidebarData={sidebarData}
          pressPageData={pressPageData}
          themeVersion={page.theme_version === 'v2' ? 'v2' : 'v1'}
          productInformation={productInformation}
          modelPortfolios={modelPortfolios}
          pressReleases={pressReleases}
          mediaArticles={mediaArticles}
          contentEntries={contentEntries}
          usProductOffers={usProductOffers}
        />
      </Layout>
    </IntlProvider>
  );
};

export const getStaticProps: GetStaticProps = async ({
  params = {},
  preview = false,
  locale: localeProp = 'en',
  locales,
}: {
  params: Record<string, any>;
  preview: boolean;
  locale: Locale;
  locales: string[];
}) => {
  const locale = getLocale(localeProp)?.intlFormat;
  const messages = await getServerSideTranslations({ locale, locales });
  try {
    const props =
      getPageType(locale, params) === PageType.BankPage
        ? await getBankPageProps(params, preview, locale)
        : await getContentStackPageProps(params, preview, locale);

    const revalidateEnv = parseInt(process.env.REVALIDATE_SECONDS ?? '60');
    const revalidate = isNaN(revalidateEnv) ? 60 : revalidateEnv;
    return {
      props: {
        ...props,
        locale,
        messages,
      },
      revalidate,
    };
  } catch (error) {
    if (error instanceof NotFoundError) return { notFound: true };
    throw error;
  }
};

export const getStaticPaths = async ({ locales }: { locales: string[] }) => {
  const filteredLocales = locales.filter((locale) => locale !== 'default');

  return {
    paths: await getPageStaticPaths(filteredLocales),
    fallback: 'blocking' as const,
  };
};

export default PageTemplate;
